// src/lrp/lrpPush.js - the LRP Push Signal economics, as ONE rule.
//
// (!) THIS IS THE ONLY PLACE THIS ARITHMETIC LIVES. The API snapshot writer
// (api/_cattle-cog.js), the admin UI (LRPCogSignalView in src/App.jsx), the
// alert path and tools/check-lrp-push.js all call THIS module. Two copies of a
// premium rule is how the quoter and the signal come to disagree about what a
// producer actually pays - this repo has that failure written down several
// times over (three news handlers, two guarantee engines, three normFarmerName
// copies).
//
// -- WHAT WENT WRONG, AND WHY EACH RULE HERE EXISTS -------------------------
//
// The v1 signal computed `margin = coverage_price - breakeven` and shipped a
// green "PUSH LRP . +$378/head" badge. Reconciled 2026-08-28 against the live
// snapshot, every displayed number was arithmetically correct and the
// conclusion was still wrong, four separate ways:
//
//   1. THE PRODUCER PREMIUM WAS NEVER SUBTRACTED. `lrp_cost_cwt` was stored and
//      displayed in its own column and never entered the margin. Worse, that
//      column is GROSS - the producer pays gross x subsidy share - so even a
//      reader who subtracted the printed number by hand would have over-charged
//      themselves by ~35%.
//   2. THERE WAS NO BASIS. LRP settles on a NATIONAL index; a NY producer sells
//      into NY. The v1 "how this is calculated" note claimed NY cash on the
//      PURCHASE side "embeds local basis vs the national LRP floor" - it does
//      not. Purchase basis and sale basis are different quantities on opposite
//      sides of the trade, and the sale side had none at all.
//   3. ONE ANIMAL WAS PRICED AGAINST EVERY ENDORSEMENT FROM 13 TO 52 WEEKS.
//      Same 275 lb of gain, same $70 of overhead, whether the cattle were held
//      13 weeks (implied ADG 3.02) or 52 (implied ADG 0.76). Those are not the
//      same production program and they cannot cost the same.
//   4. THE INPUT ANIMAL PRICE CAME FROM FOUR HEAD OF CATTLE. The 2026-08-22 NY
//      auction week carried exactly two usable weight bands - 3 head at 450 lb
//      and ONE head at 850 lb - 400 lb apart, and the model drew a straight
//      line between them. That single 850 lb head printed $201/cwt against
//      $320-332 in every well-populated week of the preceding quarter. It is a
//      different class of cattle, and it set the purchase cost under BOTH
//      segments.
//
// Defects 1 and 2 make the number too high by a knowable amount. Defect 4 makes
// it unknowable, which is worse, and is why `resolveInputAnimal` refuses thin
// prints rather than averaging them in.
//
// -- WHAT THIS MODULE WILL AND WILL NOT SAY --------------------------------
//
// It answers "do current LRP offerings protect a genuinely profitable modeled
// margin, on standardized assumptions, strongly enough to be worth a call". It
// does NOT know a producer's costs, and it never claims to. Every output is a
// MODELED, STANDARDIZED figure and the decision it emits is a call list, not a
// recommendation to bind. The handoff is deliberate:
//
//     Push Signal finds the opportunity
//       -> agent calls the producer
//       -> the producer's real numbers go into the LRP Profit Calculator
//       -> the agent runs the real quote
//
// Language rule, enforced by tools/check-lrp-push.js: "guaranteed", "lockable"
// and "locked-in margin" are FORBIDDEN in any rendered string. A figure that
// omits basis, or rests on modeled costs, is not a guarantee of anything.

"use strict";

// ==========================================================================
// 0. VERSIONING - every stored result records which rules produced it
// ==========================================================================
//
// Persisted with every row so a decision can be re-derived later, and so a
// change to any assumption is visible as a version change rather than as an
// unexplained shift in yesterday's number. tools/check-lrp-push.js asserts that
// editing a cost constant or a threshold moves the corresponding version.

const MODEL_VERSION = {
  engine: "lrp-push/2.0.0",
  cost_model: "cattle-cost-stack/2026-08-28",
  premium_model: "rma-lrp-cattle-subsidy/2020-enhancement",
  basis_model: "ny-scenario/2026-08-28",
  decision_model: "five-state/2026-08-28",
  data_quality_model: "dq/2026-08-28",
};

// ==========================================================================
// 1. PREMIUM AND SUBSIDY - the canonical rule, verified against the book
// ==========================================================================
//
// `lrp_daily_rates.cost_per_cwt` is the GROSS premium per cwt. That is not read
// off the column name - api/rma-lrp-sync.js enforces `coverage_price x rate ~=
// cost_per_cwt` as a hard ingestion invariant (it aborts the upsert on
// violation), which is RMA's own rate-file identity. Gross premium is what that
// product is.
//
// The producer pays gross x the subsidy share below.
//
// (!) VERIFIED AGAINST 221 REAL BOUND ENDORSEMENTS (2026-08-28), not asserted
// from the schedule. `lrp_endorsements` carries total_premium, subsidy and
// producer_premium as the AIP stamped them. Bucketing producer_premium /
// total_premium:
//
//     0.65 tier (>=95% coverage)   219 rows   observed 0.6458 - 0.6515
//     0.55 tier (85-89.99%)          1 row    observed 0.5476
//     off-schedule                   1 row    0.6429  ->  $36 / $56
//
// The single off-schedule row is whole-dollar rounding: 0.65 x $56 = $36.40,
// stamped $36. So 221 of 221 are consistent with this schedule.
//
// (!) AND THE HALF THAT IS *NOT* VERIFIED IS NAMED. Nobody in this book has
// ever bought below 85% coverage, so the 0.60 / 0.50 / 0.45 tiers are RMA's
// published schedule and have NO corroboration here. They are marked
// `verified:false` and a signal that depends on one cannot read Strong data
// quality.

const LRP_SUBSIDY_SCHEDULE = {
  version: "rma-lrp-cattle-2020-enhancement",
  // The 2020 enhancement changed these materially. We hold no source for the
  // pre-2021 schedule, so a reinsurance year before this is REFUSED rather than
  // priced on a guess - see lrpProducerShare.
  effective_from_ry: 2021,
  // Verified against bound endorsements through RY2027 (end dates to
  // 2027-08-25). Beyond this the schedule still applies but is flagged
  // `assumed`, which caps data quality - it does not blank the page.
  verified_through_ry: 2027,
  tiers: [
    { min_cov: 0.95, subsidy: 0.35, producer_share: 0.65, verified: true, evidence: "219 bound endorsements, 0.6458-0.6515" },
    { min_cov: 0.90, subsidy: 0.40, producer_share: 0.60, verified: false, evidence: "RMA schedule only - not observed in this book" },
    { min_cov: 0.85, subsidy: 0.45, producer_share: 0.55, verified: true, evidence: "1 bound endorsement, 0.5476" },
    { min_cov: 0.80, subsidy: 0.50, producer_share: 0.50, verified: false, evidence: "RMA schedule only - not observed in this book" },
    { min_cov: 0.00, subsidy: 0.55, producer_share: 0.45, verified: false, evidence: "RMA schedule only - not observed in this book" },
  ],
};

// Returns { share, subsidy, verified, assumed_ry } or null when the schedule
// cannot answer. Null is a NO READ, never a silent default - a wrong subsidy is
// a wrong premium is a wrong decision.
function lrpProducerShare(covLevel, reinsuranceYear) {
  const c = Number(covLevel);
  if (!Number.isFinite(c) || c <= 0 || c > 1) return null;
  const ry = reinsuranceYear == null ? null : Number(reinsuranceYear);
  // Pre-enhancement years priced a different schedule that we have not sourced.
  if (ry != null && Number.isFinite(ry) && ry < LRP_SUBSIDY_SCHEDULE.effective_from_ry) return null;
  const tier = LRP_SUBSIDY_SCHEDULE.tiers.find((t) => c >= t.min_cov);
  if (!tier) return null;
  // (!) A MISSING OR UNPARSEABLE YEAR IS ASSUMED, NOT VERIFIED (2026-10-15). The
  // share is still returned - the schedule is the best answer available - but a
  // row that cannot say which reinsurance year it prices was never checked
  // against the window the schedule was verified for, so it must say so.
  const ry_known = ry != null && Number.isFinite(ry);
  const assumed_ry = !ry_known || ry > LRP_SUBSIDY_SCHEDULE.verified_through_ry;
  return {
    share: tier.producer_share,
    subsidy: tier.subsidy,
    verified: tier.verified,
    assumed_ry,
    reinsurance_year: ry_known ? ry : null,
    schedule_version: LRP_SUBSIDY_SCHEDULE.version,
  };
}

// Producer-paid premium per cwt. THE one conversion from the stored gross.
// Returns null (not 0) when the share cannot be resolved - a missing premium
// must propagate as "cannot price", because 0 would read as a free endorsement.
function producerPremiumCwt(grossCwt, covLevel, reinsuranceYear) {
  const g = Number(grossCwt);
  if (!Number.isFinite(g) || g < 0) return null;
  const s = lrpProducerShare(covLevel, reinsuranceYear);
  if (!s) return null;
  return g * s.share;
}

// -- The two floors, kept separate because they answer different questions --
//
//   netIndexFloor       what LRP protects, after what it costs. A national
//                       index number. NOT what the producer's cheque says.
//   protectedCashFloor  netIndexFloor + expected local basis. The modeled
//                       local realisation, and the only one that belongs
//                       beside a breakeven.
//
// LRP indemnifies on the national actual ending value, so basis is the
// producer's own exposure and never disappears. Collapsing these two into one
// number is defect 2 above.

function netIndexFloor(coveragePriceCwt, producerPremCwt) {
  const cp = Number(coveragePriceCwt), pp = Number(producerPremCwt);
  if (!Number.isFinite(cp) || !Number.isFinite(pp)) return null;
  return cp - pp;
}

function protectedCashFloor(coveragePriceCwt, producerPremCwt, basisCwt) {
  const nif = netIndexFloor(coveragePriceCwt, producerPremCwt);
  const b = Number(basisCwt);
  if (nif == null || !Number.isFinite(b)) return null;
  return nif + b;
}

// ==========================================================================
// 2. BASIS - explicit, named, and never silently zero
// ==========================================================================
//
// (!) THIS IS A MEASURED BASIS, AND MEASURING IT PROPERLY TOOK TWO ATTEMPTS.
// The first attempt compared NY auction cash against `expected_end_value` from
// the rate file. That is WRONG and it flattered nothing - EEV is a FORWARD
// expectation for an end date months out, auction cash is SPOT, and the
// difference between them is basis PLUS the forward carry. In a declining
// market (which this is) the two are not separable.
//
// THE CLEAN COMPARISON IS AGAINST THE REALISED INDEX. `lrp_endorsements`
// carries `actual_end_value` - the national index as it actually settled on a
// known date. Matched to the NY auction print in the nearest week (<=14d, in
// practice <=10d), same weight band, steers only, both $/cwt, and only weeks
// with >=5 head. Measured 2026-08-28 over 2025-02 to 2026-07:
//
//     n = 11    mean -$30.67/cwt    median -$36.22/cwt
//     range -$3.31 (2026-07-06) to -$58.74 (2026-03-24)
//
// THE SIGN IS THE FINDING AND IT IS LARGE. On an 8 cwt animal the mean is
// -$245/head. LRP indemnifies on an index a NY seller does not receive, and v1
// carried none of that. Not one of the 11 observations was materially positive.
//
// (!) WHY IT IS STILL "measured" AND NOT "observed": n = 11 over 17 months, and
// `lrp_endorsements` does not carry a commodity type on these rows - one end
// date (2025-07-14) shows TWO different actual ending values, so at least one
// AEV in that pair belongs to a different class than the band it was matched
// to. That pair's outlier (+$19.19) is the only positive in the set and is
// excluded as unattributable rather than averaged in. Basis therefore caps data
// quality at Limited and can never make a read Strong.
//
// `zero` exists for comparison and is LABELLED as a deliberate choice - a
// silent zero basis is the defect, and the measurement says zero is wrong.

const BASIS_MEASUREMENT = {
  version: "ny-feeder-basis/2026-08-28",
  source: "lrp_endorsements.actual_end_value vs feeder_weekly NY, matched weight band, >=5 head, <=14d",
  n: 11, mean_cwt: -30.67, median_cwt: -36.22, min_cwt: -58.74, max_cwt: -3.31,
  window: "2025-02-24 to 2026-07-06",
  excluded: "1 observation (+$19.19, 2025-07-14) - two conflicting AEVs on one end date, class unattributable",
};

const BASIS_SCENARIOS = {
  zero: {
    key: "zero",
    label: "Zero basis (index = local)",
    cwt: 0,
    provenance: "assumed",
    verified: false,
    note: "Deliberate comparison case. Assumes NY cash equals the national index. The measurement contradicts it - not one of 11 observations was materially positive. Shown for contrast, never as a default.",
  },
  ny_optimistic: {
    key: "ny_optimistic",
    label: "NY feeder basis - narrow",
    cwt: -10,
    provenance: "measured",
    verified: true,
    note: "Narrow end of the 11 measured observations (the three narrowest were -$3.31, -$10.24, -$12.92).",
  },
  ny_standard: {
    key: "ny_standard",
    label: "NY feeder basis - typical",
    cwt: -31,
    provenance: "measured",
    verified: true,
    note: "Mean of 11 measured NY observations against the REALISED national index (actual ending value), 2025-02 to 2026-07.",
  },
  ny_conservative: {
    key: "ny_conservative",
    label: "NY feeder basis - wide",
    cwt: -45,
    provenance: "measured",
    verified: true,
    note: "Between the measured median (-$36.22) and the worst observation (-$58.74).",
  },
};

// (!) THE MEASUREMENT ABOVE IS NY *FEEDER* STEERS AND NOTHING ELSE (2026-10-15).
// Every one of the 11 observations is a feeder_weekly NY print against a feeder
// index. FED CATTLE BASIS HAS NEVER BEEN MEASURED here, and no sourced
// Northeast fed-cattle basis was found (a web check 2026-10-15 turned up only
// Plains / Southeast extension material). The fed segment still CARRIES the
// feeder numbers - dropping them would silently zero basis, which is the
// defect this section exists to forbid - but it must not CALL them measured.
// `provenance: "borrowed"` and its own label and reason say exactly what they
// are, and data quality stays capped at Limited. Rule zero: no fed basis is
// invented, and a borrowed one is never presented as a measurement.
const BASIS_MEASURED_SEGMENTS = new Set(["feeder"]);
const BORROWED_BASIS_NOTE = "borrowed from the NY feeder measurement - fed basis not measured";

function resolveBasis(key, segment) {
  const b = BASIS_SCENARIOS[key];
  if (!b) return null; // an unknown scenario is NO READ, not zero
  const out = Object.assign({}, b);
  if (segment != null && !BASIS_MEASURED_SEGMENTS.has(segment) && out.provenance === "measured") {
    out.provenance = "borrowed";
    out.verified = false;
    out.borrowed_from = "feeder";
    out.label = out.label + " (" + BORROWED_BASIS_NOTE + ")";
    out.note = BORROWED_BASIS_NOTE + ". " + out.note;
  }
  return out;
}

// ==========================================================================
// 3. INPUT-ANIMAL PRICE - refuse a thin print, never average it in
// ==========================================================================
//
// Defect 4. NY auction volume is genuinely small and genuinely lumpy, so the
// rule is not "clean the data", it is "say how good the read is, and refuse
// when it is not a read at all".
//
// Measured on the live table 2026-08-28:
//   week 2026-08-22   STEER_4_5 $364.86 (3 hd)   STEER_8_9 $201.00 (1 hd)
//   week 2026-08-08   4_5 $440.01 (17)  5_6 $383.60 (25)  6_7 $369.20 (10)
//                     7_8 $321.24 (22)  8_9 $320.00 (3)
//
// The single 850 lb head at $201 is the whole problem: every well-populated
// week that quarter printed $320-332 there. One head is not a market, and a
// straight line from it across a 400 lb gap is not a weight slide.

const BAND_MID_LB = { STEER_4_5: 450, STEER_5_6: 550, STEER_6_7: 650, STEER_7_8: 750, STEER_8_9: 850 };

const CASH_RULES = {
  version: "ny-cash/2026-08-28",
  min_head_per_band: 5, // one head is not a market
  max_band_gap_lb: 200, // bracketing bands further apart interpolate across too much shape
  max_week_age_days: 21, // ~3 auction weeks; beyond this the print is ageing
  stale_week_age_days: 35, // beyond this it is not a current market at all
  max_weeks_back: 8, // how far to search for a usable week
};

// Linear interpolation on the weight slide, but only between two bands that
// actually BRACKET the target. Clamping to an end band is extrapolation and is
// reported as such rather than passed off as an interpolation.
function interpolateSlide(points, targetLb) {
  if (!points || !points.length) return null;
  const p = points.slice().sort((a, b) => a.w - b.w);
  if (p.length === 1) return { price: p[0].price, method: "single_band", lo: p[0], hi: p[0], gap: 0 };
  if (targetLb <= p[0].w) return { price: p[0].price, method: "clamped_low", lo: p[0], hi: p[0], gap: 0 };
  if (targetLb >= p[p.length - 1].w) {
    const last = p[p.length - 1];
    return { price: last.price, method: "clamped_high", lo: last, hi: last, gap: 0 };
  }
  for (let i = 1; i < p.length; i++) {
    if (targetLb <= p[i].w) {
      const a = p[i - 1], b = p[i];
      const gap = b.w - a.w;
      return {
        price: a.price + ((targetLb - a.w) / gap) * (b.price - a.price),
        method: "interpolated", lo: a, hi: b, gap,
      };
    }
  }
  return null;
}

// rows: [{series, avg_price, head_count, week_ending}] - any number of weeks.
// Walks back week by week to the most recent week that can actually answer, and
// reports which week that was and how good it is. Falling back to an older week
// is visible; falling back to EEV is visible and separately labelled.
function resolveInputAnimal(rows, targetLb, opts) {
  const o = Object.assign({}, CASH_RULES, opts || {});
  const asOf = o.asOf ? new Date(o.asOf + "T00:00:00Z") : null;
  const weeks = [...new Set((rows || []).map((r) => r.week_ending))].sort().reverse();
  const rejected = [];

  for (const wk of weeks.slice(0, o.max_weeks_back)) {
    const all = (rows || []).filter((r) => r.week_ending === wk && BAND_MID_LB[r.series] != null && r.avg_price != null);
    const usable = all.filter((r) => Number(r.head_count || 0) >= o.min_head_per_band);
    const dropped = all
      .filter((r) => Number(r.head_count || 0) < o.min_head_per_band)
      .map((r) => ({ series: r.series, price: Number(r.avg_price), head: Number(r.head_count || 0) }));

    if (usable.length < 2) {
      rejected.push({ week: wk, reason: "only " + usable.length + " band(s) with >=" + o.min_head_per_band + " head", dropped });
      continue;
    }
    const pts = usable.map((r) => ({ w: BAND_MID_LB[r.series], price: Number(r.avg_price), head: Number(r.head_count || 0), series: r.series }));
    const interp = interpolateSlide(pts, targetLb);
    if (!interp) { rejected.push({ week: wk, reason: "interpolation failed", dropped }); continue; }

    const ageDays = asOf ? Math.round((asOf - new Date(wk + "T00:00:00Z")) / 86400000) : 0;
    let quality = "strong";
    const caveats = [];
    if (interp.method !== "interpolated") {
      quality = "limited";
      caveats.push(targetLb + " lb sits outside the usable bands - price " + interp.method.replace("_", " ") + " at " + interp.lo.series);
    } else if (interp.gap > o.max_band_gap_lb) {
      quality = "limited";
      caveats.push("bracketing bands are " + interp.gap + " lb apart (limit " + o.max_band_gap_lb + ")");
    }
    if (ageDays > o.stale_week_age_days) { quality = "stale"; caveats.push("auction week is " + ageDays + " days old"); }
    else if (ageDays > o.max_week_age_days) { if (quality === "strong") quality = "limited"; caveats.push("auction week is " + ageDays + " days old"); }
    if (dropped.length) caveats.push(dropped.length + " band(s) dropped below " + o.min_head_per_band + " head: " + dropped.map((d) => d.series + " $" + d.price + " on " + d.head + " hd").join(", "));
    // Having to reach past a more recent week means we are NOT reading the
    // current market, whatever the age arithmetic says. That is a limited read
    // by construction - the live case (2026-08-28) skipped two weeks whose only
    // prints were 3 head and 1 head, and calling that Strong would hide exactly
    // the thinness that made the skip necessary.
    if (rejected.length) {
      if (quality === "strong") quality = "limited";
      caveats.push(rejected.length + " more recent week(s) unusable: " + rejected.map((r) => r.week + " (" + r.reason + ")").join("; "));
    }

    return {
      price_cwt: interp.price,
      week_ending: wk,
      age_days: ageDays,
      quality,
      source: "ny_auction_cash",
      method: interp.method,
      bands_used: pts.map((x) => ({ series: x.series, mid_lb: x.w, price: x.price, head: x.head })),
      bracket: interp.method === "interpolated" ? { lo: interp.lo.series, hi: interp.hi.series, gap_lb: interp.gap } : null,
      dropped_bands: dropped,
      rejected_weeks: rejected,
      caveats,
    };
  }

  return {
    price_cwt: null, quality: "insufficient", source: "ny_auction_cash", week_ending: weeks[0] || null,
    bands_used: [], dropped_bands: [], rejected_weeks: rejected,
    caveats: ["No auction week in the search window carried two usable weight bands."],
  };
}

// The EEV backstop is a DIFFERENT ECONOMIC QUANTITY - a futures-implied forward
// expectation for the national index, not what a NY buyer pays at a barn today.
// It is allowed so the page is not blank, it is labelled `fallback`, and the
// decision logic refuses to reach PUSH NOW or CONTACT on it.
function eevFallback(eevCwt) {
  const v = Number(eevCwt);
  if (!Number.isFinite(v) || v <= 0) {
    return { price_cwt: null, quality: "insufficient", source: "eev_fallback", bands_used: [], dropped_bands: [], rejected_weeks: [], caveats: ["No expected end value available either."] };
  }
  return {
    price_cwt: v, quality: "fallback", source: "eev_fallback", week_ending: null, bands_used: [], dropped_bands: [], rejected_weeks: [],
    caveats: ["NY auction cash unusable - substituted the LRP expected end value. That is a national futures-implied forward, NOT a local purchase cost; it carries no NY basis and no weight slide."],
  };
}

// ==========================================================================
// 4. THE PRODUCTION PROGRAM AND ITS TIMELINE
// ==========================================================================
//
// Defect 3. An endorsement is a DATE, and a set of cattle reaches market on a
// date. v1 offered every endorsement from 13 to 52 weeks as an equal
// alternative for the same animal, charging identical cost each time.
//
// (!) THE ELIGIBILITY RULE IS RMA'S OWN 60-DAY MARKETING WINDOW, NOT A TOLERANCE
// PICKED TO PRODUCE RESULTS. The LRP basic provisions require ownership until
// the last 60 days before the End Date; selling MORE than 60 days early voids
// the coverage outright - no indemnity, no premium refund. That rule is already
// verified in this repo (see project_lrp_profit_calc, sourced from the RMA LRP
// handbook and basic policy) and it is exactly the constraint an endorsement
// date has to satisfy to be a real option for a set of cattle.
//
// So: an endorsement is eligible when the modeled market-ready date falls
// inside [end_date - 60, end_date + grace]. `grace_days` is configurable and
// defaults to 0. Selling AFTER the end date is permitted by RMA (the indemnity
// settles at the end date regardless) but leaves the animal unprotected between
// the two, so it is off by default rather than quietly allowed.

const MARKETING_WINDOW_DAYS = 60; // RMA LRP basic provisions
const TIMELINE_RULES = { version: "timeline/2026-08-28", window_days: MARKETING_WINDOW_DAYS, grace_days: 0 };

function addDays(isoDate, days) {
  const d = new Date(isoDate + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + Math.round(days));
  return d.toISOString().slice(0, 10);
}
function daysBetween(aIso, bIso) {
  return Math.round((new Date(bIso + "T00:00:00Z") - new Date(aIso + "T00:00:00Z")) / 86400000);
}

function requiredDaysOnFeed(gainLb, adg) {
  const g = Number(gainLb), a = Number(adg);
  if (!Number.isFinite(g) || !Number.isFinite(a) || a <= 0 || g < 0) return null;
  return g / a;
}
// The v1 diagnostic, kept as a first-class output: what ADG would this
// endorsement REQUIRE? A 52-week endorsement on 275 lb of gain demands 0.76
// lb/day, which is not a backgrounding program, and the page now says so.
function impliedAdg(gainLb, days) {
  const g = Number(gainLb), d = Number(days);
  if (!Number.isFinite(g) || !Number.isFinite(d) || d <= 0) return null;
  return g / d;
}

// Why an end date is or is not a real option for these cattle. Never silently
// drops one - the reason is carried so the UI can show the near misses.
function endorsementTimingFit(asOf, endDate, gainLb, adg, opts) {
  const o = Object.assign({}, TIMELINE_RULES, opts || {});
  const days = requiredDaysOnFeed(gainLb, adg);
  if (days == null || !endDate) return { eligible: false, reason: "no_timeline", ready_date: null };
  const readyDate = addDays(asOf, days);
  const windowOpens = addDays(endDate, -o.window_days);
  const latest = addDays(endDate, o.grace_days);
  const offsetDays = daysBetween(readyDate, endDate); // + = endorsement ends after cattle are ready
  const eligible = readyDate >= windowOpens && readyDate <= latest;
  let reason;
  if (eligible) reason = "ready inside the RMA " + o.window_days + "-day marketing window";
  else if (readyDate < windowOpens) reason = "cattle market-ready " + daysBetween(readyDate, windowOpens) + "d before the window opens - selling then voids the endorsement";
  else reason = "cattle not market-ready until " + daysBetween(latest, readyDate) + "d after the end date";
  return {
    eligible, reason, ready_date: readyDate, window_opens: windowOpens,
    days_on_feed: days, offset_days: offsetDays,
    implied_adg_to_hit_end_date: impliedAdg(gainLb, daysBetween(asOf, endDate)),
  };
}

// The marketed animal must also fit the LRP type's own weight band, or the
// endorsement does not cover these cattle at all.
function weightClassFits(outWtCwt, lowCwt, highCwt) {
  // (!) `Number(null)` IS 0, NOT NaN. Taking Number() first made an unpublished
  // weight band read as a 0-0 cwt band, which excludes every animal — so a type
  // RMA had not published limits for would have silently dropped its whole
  // segment with "outside this type's 0-0 cwt band" as the reason. Caught by the
  // checker's own both-ways assertion (a null band must NOT be enforced), which
  // is the case a fires-on-the-bad-row-only test never reaches.
  const num = (v) => (v == null || v === "" ? NaN : Number(v));
  const w = num(outWtCwt), lo = num(lowCwt), hi = num(highCwt);
  if (!Number.isFinite(w)) return { fits: false, reason: "no sale weight" };
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { fits: true, reason: "type weight band not published - not enforced" };
  if (w < lo || w > hi) return { fits: false, reason: "sale weight " + w + " cwt is outside this type's " + lo + "-" + hi + " cwt band" };
  return { fits: true, reason: "sale weight " + w + " cwt inside the " + lo + "-" + hi + " cwt band" };
}

// ==========================================================================
// 5. THE COST STACK - named lines, counted once, time-dependent
// ==========================================================================
//
// v1 carried one unexplained constant per segment (`overheadHd` $235 fed / $70
// feeder) that silently stood in for yardage, interest, death loss, health,
// freight and marketing, and did not move with time held. Every line below is
// named, unit-labelled, configurable, versioned and added exactly once.
//
// -- PROVENANCE, STATED HONESTLY -------------------------------------------
//
// COST OF GAIN is the one leg with real grounding in this repo. The 2026-06-29
// work measured ISU Estimated Returns (24 years, 2002-2026) and established the
// corn-driven structure (~$0.08/lb-gain per $1/bu corn), and validated the
// commercial end against Kevin's own WMS/Sterling closeouts ($1.09-1.11/lb fed
// at corn ~$4.25). It also established the finding that matters here: COG spans
// a ~2x RANGE by operation (Iowa farmer-feeder $0.50-0.57 vs commercial $1.09).
//
// That measured range is what the three scenarios are built from. They are NOT
// arbitrary multipliers on one number - `efficient` is the ISU farmer-feeder
// end, `conservative` is the commercial closeout end, `standard` sits between.
//
// (!) THE NON-FEED LINES ARE EXTENSION-BUDGET TYPICAL VALUES AND ARE NOT
// VALIDATED AGAINST THIS BOOK. Yardage, interest, death loss, health, freight
// and commission are marked `verified:false`, they are configurable, and the
// data-quality read reflects that they are assumptions. Saying "conservative"
// does not make them conservative - Phase 4 of this work is to test them
// against real closeouts, and until that happens the claim is not made.

const COST_MODEL_VERSION = MODEL_VERSION.cost_model;

const SCENARIOS = {
  efficient: {
    key: "efficient", label: "Efficient",
    blurb: "Well-performing lower-cost operator. Cost of gain at the ISU Iowa farmer-feeder end of the measured range.",
    cog_anchor: "isu_farmer_feeder", cog_scale: 0.55,
    adg: { feeder: 2.6, fed: 3.6 },
    yardage_per_day: { feeder: 0.25, fed: 0.35 },
    interest_apr: 0.070,
    death_loss_pct: { feeder: 0.010, fed: 0.008 },
    health_per_hd: { feeder: 18, fed: 22 },
    trucking_per_hd: { feeder: 22, fed: 30 },
    commission_pct_of_sale: 0.020,
    checkoff_per_hd: 2,
    basis_scenario: "ny_optimistic",
  },
  standard: {
    key: "standard", label: "Standard",
    blurb: "Reasonable commercial / regional baseline. Cost of gain mid-range between the ISU and commercial-closeout ends.",
    cog_anchor: "midpoint", cog_scale: 0.78,
    adg: { feeder: 2.0, fed: 3.3 },
    yardage_per_day: { feeder: 0.35, fed: 0.45 },
    interest_apr: 0.080,
    death_loss_pct: { feeder: 0.020, fed: 0.012 },
    health_per_hd: { feeder: 25, fed: 30 },
    trucking_per_hd: { feeder: 30, fed: 38 },
    commission_pct_of_sale: 0.025,
    checkoff_per_hd: 2,
    basis_scenario: "ny_standard",
  },
  conservative: {
    key: "conservative", label: "Conservative",
    blurb: "Defensibly adverse. Cost of gain at the commercial-closeout end validated against Kevin's own WMS/Sterling closeouts, slower gain, wider basis.",
    cog_anchor: "commercial_closeout", cog_scale: 1.00,
    adg: { feeder: 1.6, fed: 2.9 },
    yardage_per_day: { feeder: 0.45, fed: 0.55 },
    interest_apr: 0.090,
    death_loss_pct: { feeder: 0.030, fed: 0.015 },
    health_per_hd: { feeder: 35, fed: 40 },
    trucking_per_hd: { feeder: 38, fed: 46 },
    commission_pct_of_sale: 0.030,
    checkoff_per_hd: 2,
    basis_scenario: "ny_conservative",
  },
};
const SCENARIO_ORDER = ["efficient", "standard", "conservative"];

// The standardized programs. `out_wt_cwt` is what gets marketed and must sit
// inside the LRP type's own weight band.
const PROGRAMS = {
  feeder: {
    key: "feeder", label: "Feeder sellers", sub: "cow-calf / backgrounder selling feeders",
    lrp_type_label: "Steers Wt 2", in_wt_lb: 525, out_wt_cwt: 8.0, gain_lb: 275,
  },
  fed: {
    key: "fed", label: "Fed cattle", sub: "feedlot placing cattle now",
    lrp_type_label: "Fed Cattle", in_wt_lb: 800, out_wt_cwt: 15.0, gain_lb: 700,
  },
};

// Cost of gain at the COMMERCIAL CLOSEOUT end ($/lb gain), corn/hay/soymeal
// driven. These are the v1 constants, retained deliberately and unchanged
// because they are the one leg that WAS validated (engine fed breakeven
// reproduced the WMS closeout within $1.34/cwt, 2026-06-29). `cog_scale` then
// walks that anchor down toward the measured ISU end for the cheaper scenarios.
function fedCogCommercial(corn, hay, soymeal) {
  return 0.093 * corn + 0.00086 * hay + 0.00030 * soymeal + 0.41;
}
function feederCogCommercial(corn, hay, soymeal) {
  return 0.055 * corn + 0.00130 * hay + 0.00020 * soymeal + 0.40;
}
function cogPerLb(segment, scenarioKey, feed) {
  const sc = SCENARIOS[scenarioKey];
  if (!sc || !feed) return null;
  const corn = Number(feed.corn), hay = Number(feed.hay), soymeal = Number(feed.soymeal);
  if (![corn, hay, soymeal].every(Number.isFinite)) return null;
  const anchor = segment === "fed" ? fedCogCommercial(corn, hay, soymeal) : feederCogCommercial(corn, hay, soymeal);
  return anchor * sc.cog_scale;
}

// The full ownership cost of one head, itemized. Returns $/head lines plus the
// breakeven per cwt of the MARKETED animal.
//
// (!) `cost of gain` AND `total cost during ownership` ARE DIFFERENT THINGS and
// the return distinguishes them. Cost of gain is feed only. Everything else -
// yardage, interest, health, freight, marketing, death loss - is a cost of
// OWNING the animal for a period, and it is what scales with time held.
// The ownership costs BEYOND FEED for one head — yardage, vet/health, trucking,
// interest on the cattle (whole period) and on the feed (half period), marketing
// commission and checkoff. ONE rule with two callers: buildCostStack below and
// the LRP Profit Calculator's "typical ownership costs" offer, so the two cannot
// come to disagree about what owning an animal costs.
//
// ⚠ A MISSING SALE VALUE IS NaN, NEVER 0. `Number(null)` is 0 and 0 is finite,
// so a caller passing null used to get a $0 commission instead of the documented
// fallback — every Push Signal row carried commission: 0 from 2026-08-28 to
// 2026-10-15, overstating every margin by the commission.
function ownershipCostsBeyondFeed(segment, scenarioKey, inputs) {
  const sc = SCENARIOS[scenarioKey];
  if (!sc || !sc.adg[segment]) return null;
  const o = inputs || {};
  const fin = (v) => (v == null || v === "" ? NaN : Number(v));
  const days = fin(o.days), purchase = fin(o.purchase_hd), feed = fin(o.feed_hd), saleHd = fin(o.sale_hd);
  if (!(Number.isFinite(days) && days >= 0) || !Number.isFinite(purchase) || !Number.isFinite(feed)) return null;
  const exclude = new Set(o.exclude || []);
  // Per-line RATE overrides — the Profit Calculator's Detailed mode lets a producer
  // type their own yardage, vet, trucking, interest, commission and checkoff. They
  // run through THIS rule rather than a second copy of it. An override that is not
  // a finite, non-negative number is ignored (the scenario's value stands) and is
  // named in `ignored_overrides`, never silently read as 0.
  const ov = o.rates || {};
  const ignored = [];
  const pick = (key, base) => {
    if (ov[key] == null || ov[key] === "") return base;
    const v = Number(ov[key]);
    if (Number.isFinite(v) && v >= 0) return v;
    ignored.push(key);
    return base;
  };
  const R = {
    yardage_per_day: pick("yardage_per_day", sc.yardage_per_day[segment]),
    health_per_hd: pick("health_per_hd", sc.health_per_hd[segment]),
    trucking_per_hd: pick("trucking_per_hd", sc.trucking_per_hd[segment]),
    interest_apr: pick("interest_apr", sc.interest_apr),
    commission_pct_of_sale: pick("commission_pct_of_sale", sc.commission_pct_of_sale),
    checkoff_per_hd: pick("checkoff_per_hd", sc.checkoff_per_hd),
  };
  const commissionOnSale = Number.isFinite(saleHd) && saleHd > 0;
  // Commission is a share of the SALE. Without a sale value it is charged against
  // the purchase value rather than skipped — a $0 commission is a claim, not a default.
  const commissionBase = commissionOnSale ? saleHd : purchase;
  const all = [
    { key: "yardage", label: "Yardage / maintenance", value: R.yardage_per_day * days,
      detail: Math.round(days) + " d @ $" + R.yardage_per_day.toFixed(2) + "/d", at_risk: true },
    { key: "health", label: "Vet / health", value: R.health_per_hd, detail: "processing + treatment", at_risk: true },
    { key: "trucking", label: "Trucking", value: R.trucking_per_hd, detail: "in + out", at_risk: true },
    { key: "interest_cattle", label: "Interest on cattle", value: purchase * R.interest_apr * (days / 365),
      detail: (R.interest_apr * 100).toFixed(1) + "% x " + Math.round(days) + " d", at_risk: true },
    { key: "interest_feed", label: "Interest on feed", value: feed * R.interest_apr * (days / 365) * 0.5,
      detail: (R.interest_apr * 100).toFixed(1) + "% x " + Math.round(days) + " d, half-period", at_risk: true },
    { key: "commission", label: "Marketing commission", value: R.commission_pct_of_sale * commissionBase,
      detail: (R.commission_pct_of_sale * 100).toFixed(1) + "% of " + (commissionOnSale ? "sale" : "purchase value (no sale value given)"), at_risk: false },
    { key: "checkoff", label: "Beef checkoff", value: R.checkoff_per_hd, detail: "statutory", at_risk: false },
  ];
  const lines = all.filter((l) => !exclude.has(l.key));
  return {
    scenario: scenarioKey, segment, cost_model_version: COST_MODEL_VERSION,
    lines, excluded: all.filter((l) => exclude.has(l.key)).map((l) => l.key),
    at_risk_total: lines.filter((l) => l.at_risk).reduce((a, l) => a + l.value, 0),
    total: lines.reduce((a, l) => a + l.value, 0),
    commission_base: commissionOnSale ? "sale" : "purchase",
    rates: R, ignored_overrides: ignored,
    verified: false,
  };
}

function buildCostStack(segment, scenarioKey, inputs) {
  const prog = PROGRAMS[segment], sc = SCENARIOS[scenarioKey];
  if (!prog || !sc) return null;
  const inPriceCwt = Number(inputs.in_price_cwt);
  const rawSale = inputs.sale_price_cwt; // for the commission line — null means "not given", never $0
  const salePriceCwt = rawSale == null || rawSale === "" ? NaN : Number(rawSale);
  if (!Number.isFinite(inPriceCwt)) return null;

  const cog = cogPerLb(segment, scenarioKey, inputs.feed);
  if (cog == null) return null;

  const adg = sc.adg[segment];
  const days = requiredDaysOnFeed(prog.gain_lb, adg);
  if (days == null) return null;

  const inWtCwt = prog.in_wt_lb / 100;
  const purchase = inPriceCwt * inWtCwt;
  const feedGain = cog * prog.gain_lb;
  // Interest on the purchased animal runs the whole period; interest on feed
  // accrues as it is fed, so it runs on average half the period. Both scale
  // with days held - that is the point of separating them from a constant.
  // Commission is a share of the SALE; without a sale price it is charged against
  // the purchase value rather than skipped (ownershipCostsBeyondFeed).
  const own = ownershipCostsBeyondFeed(segment, scenarioKey, {
    days, purchase_hd: purchase, feed_hd: feedGain,
    sale_hd: Number.isFinite(salePriceCwt) ? salePriceCwt * prog.out_wt_cwt : null,
  });
  if (!own) return null;
  const ln = (k) => own.lines.find((l) => l.key === k).value;
  const yardage = ln("yardage"), health = ln("health"), trucking = ln("trucking");
  const interestCattle = ln("interest_cattle"), interestFeed = ln("interest_feed");
  const commission = ln("commission"), checkoff = ln("checkoff");

  // Death loss spreads the lost animal's cost over the survivors, which is the
  // convention the LRP Profit Calculator already uses. Applied ONCE, to the
  // at-risk subtotal, and never again below.
  const atRisk = purchase + feedGain + yardage + health + trucking + interestCattle + interestFeed;
  const loaded = atRisk / (1 - sc.death_loss_pct[segment]);
  const deathLoss = loaded - atRisk;

  const totalHd = loaded + commission + checkoff;
  const breakevenCwt = totalHd / prog.out_wt_cwt;

  return {
    scenario: scenarioKey, segment, cost_model_version: COST_MODEL_VERSION,
    adg, days_on_feed: days, cog_per_lb: cog,
    lines: [
      { key: "purchase", label: "Purchase animal", unit: "$/hd", value: purchase, detail: prog.in_wt_lb + " lb @ $" + inPriceCwt.toFixed(2) + "/cwt" },
      { key: "feed_gain", label: "Feed (cost of gain)", unit: "$/hd", value: feedGain, detail: prog.gain_lb + " lb @ $" + cog.toFixed(3) + "/lb" },
      { key: "yardage", label: "Yardage / maintenance", unit: "$/hd", value: yardage, detail: Math.round(days) + " d @ $" + sc.yardage_per_day[segment].toFixed(2) + "/d" },
      { key: "health", label: "Vet / health", unit: "$/hd", value: health, detail: "processing + treatment" },
      { key: "trucking", label: "Trucking", unit: "$/hd", value: trucking, detail: "in + out" },
      { key: "interest_cattle", label: "Interest on cattle", unit: "$/hd", value: interestCattle, detail: (sc.interest_apr * 100).toFixed(1) + "% x " + Math.round(days) + " d" },
      { key: "interest_feed", label: "Interest on feed", unit: "$/hd", value: interestFeed, detail: (sc.interest_apr * 100).toFixed(1) + "% x " + Math.round(days) + " d, half-period" },
      { key: "death_loss", label: "Death loss", unit: "$/hd", value: deathLoss, detail: (sc.death_loss_pct[segment] * 100).toFixed(1) + "% spread over survivors" },
      { key: "commission", label: "Marketing commission", unit: "$/hd", value: commission, detail: own.lines.find((l) => l.key === "commission").detail },
      { key: "checkoff", label: "Beef checkoff", unit: "$/hd", value: checkoff, detail: "statutory" },
    ],
    cost_of_gain_hd: feedGain,
    total_cost_hd: totalHd,
    out_wt_cwt: prog.out_wt_cwt,
    breakeven_cwt: breakevenCwt,
    assumptions_verified: false,
    assumptions_note: "Cost of gain is anchored on the 2026-06-29 ISU/commercial-closeout measurement. The non-feed lines are extension-budget typical values and are NOT yet validated against this book's closeouts.",
  };
}

// ==========================================================================
// 6. DATA QUALITY - a fallback input must not wear a direct observation's badge
// ==========================================================================
//
// Five factual states, worst-wins. This is not a confidence score and it is not
// a verdict on the market; it says how good the READ is. The rule that matters:
// an EEV fallback and a current well-populated auction print cannot both be
// Strong, because they are not the same kind of evidence.

const DQ_ORDER = ["strong", "limited", "fallback", "stale", "insufficient"];
const DQ_RANK = { strong: 0, limited: 1, fallback: 2, stale: 3, insufficient: 4 };
function worstQuality(list) {
  let w = "strong";
  for (const q of list) if (DQ_RANK[q] != null && DQ_RANK[q] > DQ_RANK[w]) w = q;
  return w;
}

// Thresholds are derived from each series' OWN PUBLICATION CADENCE, not picked.
// `dmc_margins` is MONTHLY and is dated to the first of the month it describes,
// and USDA publishes a month about a month in arrears - so on any given day the
// newest row is legitimately 30-90 days old by that dating. "Limited" starts
// once it is older than that normal lag; "stale" means a whole publication has
// been MISSED. Setting the stale bar at the normal lag would have every summer
// day read NO READ on a perfectly healthy monthly feed.
// RMA's standard LRP endorsement lengths, in weeks. Source: University of Missouri
// Extension G459 (rev. July 2026), "13, 17, 21, 26, 30, 34, 39, 43, 47 and 52";
// every complete Steers Wt 2 and Fed Cattle grid from 2026-10-01..15 carries
// exactly these ten. A grid MISSING one cannot price that end date. Measured
// 2026-06-01..10-15: Steers Wt 2 lacked a length on 6 of 86 files (the 52-week on
// 2026-06-02, 08-28, 08-31, 09-01 and 10-02; seven lengths on 06-04) and Fed
// Cattle on 0 of 87 — and nothing said so, because the end dates that WERE present
// were whole. Whether RMA did not offer the length that day or the ingestion
// dropped it is NOT established, so the caveat names the gap, not a cause.
const STANDARD_ENDORSEMENT_WEEKS = [13, 17, 21, 26, 30, 34, 39, 43, 47, 52];

const DQ_RULES = {
  version: MODEL_VERSION.data_quality_model,
  max_rate_age_days: 4, // rate files publish each trading day; a long weekend is 3
  feed_limited_age_days: 75, // beyond the normal month-start + one-month-lag window
  feed_stale_age_days: 120, // a missed monthly publication
  min_coverage_levels: 6, // a ragged grid is a partial ingestion, not a market
};

function assessDataQuality(parts) {
  const reasons = [];
  const q = [];

  // -- LRP rates ---------------------------------------------------------
  if (!parts.rate_date) { q.push("insufficient"); reasons.push("no LRP rate file date"); }
  else {
    const age = daysBetween(parts.rate_date, parts.as_of);
    if (age > DQ_RULES.max_rate_age_days) { q.push("stale"); reasons.push("LRP rates are " + age + "d old (" + parts.rate_date + ")"); }
    else if (age > 1) { q.push("limited"); reasons.push("LRP rates are from " + parts.rate_date + ", " + age + "d before this run"); }
  }
  // (!) A PARTIAL RATE GRID FAILS CLOSED. Measured 2026-08-25: three feeder end
  // dates carried ONE coverage level instead of twelve, and the v1 engine
  // computed on it and reported a best margin as though the read were complete.
  if (parts.ragged_end_dates && parts.ragged_end_dates.length) {
    q.push("insufficient");
    reasons.push("partial rate grid - " + parts.ragged_end_dates.length + " end date(s) carry fewer than " + DQ_RULES.min_coverage_levels + " coverage levels: " + parts.ragged_end_dates.map((r) => r.end_date + " (" + r.n + ")").join(", "));
  }

  // A wholly missing end date cannot be priced, so the best endorsement may be
  // the one that is not in today's file. LIMITED, not insufficient — every end
  // date that IS present is complete and can be read.
  if (parts.missing_lengths && parts.missing_lengths.length) {
    q.push("limited");
    reasons.push("rate grid is missing the " + parts.missing_lengths.join(", ") + "-week endorsement" + (parts.missing_lengths.length > 1 ? "s" : "") +
      " - RMA offers " + STANDARD_ENDORSEMENT_WEEKS.join(", ") + " weeks, so the best end date may not be in today's file");
  }

  // -- input animal ------------------------------------------------------
  if (parts.cash) {
    q.push(parts.cash.quality);
    if (parts.cash.quality !== "strong") reasons.push("input animal: " + (parts.cash.caveats || []).join("; "));
  } else { q.push("insufficient"); reasons.push("no input-animal price"); }

  // -- feed --------------------------------------------------------------
  if (!parts.feed_date) { q.push("insufficient"); reasons.push("no feed price date"); }
  else {
    const age = daysBetween(parts.feed_date, parts.as_of);
    if (age > DQ_RULES.feed_stale_age_days) { q.push("stale"); reasons.push("feed prices are " + age + "d old (" + parts.feed_date + ") - a monthly publication has been missed"); }
    else if (age > DQ_RULES.feed_limited_age_days) { q.push("limited"); reasons.push("feed prices are from " + parts.feed_date + ", " + age + "d back (monthly series, normal lag is ~30-90d)"); }
  }

  // -- premium / subsidy -------------------------------------------------
  if (parts.premium_unverified_tier) { q.push("limited"); reasons.push("subsidy tier for this coverage level is RMA-published but not observed in this book"); }
  if (parts.premium_assumed_ry) { q.push("limited"); reasons.push("reinsurance year is missing or beyond RY" + LRP_SUBSIDY_SCHEDULE.verified_through_ry + ", the year the subsidy schedule was verified through - the producer premium rests on an assumed schedule"); }

  // -- basis -------------------------------------------------------------
  // Basis is never observed today, so it can never make the read Strong. That
  // is a statement about the data, not a hedge.
  if (!parts.basis) { q.push("insufficient"); reasons.push("no basis scenario selected"); }
  else if (parts.basis.provenance === "borrowed") { q.push("limited"); reasons.push("basis is " + BORROWED_BASIS_NOTE + " ($" + parts.basis.cwt + "/cwt applied from " + BASIS_MEASUREMENT.n + " NY feeder observations) - not a fed-cattle basis"); }
  else if (parts.basis.provenance !== "observed") { q.push("limited"); reasons.push("basis is " + parts.basis.provenance + " (" + parts.basis.label + ", $" + parts.basis.cwt + "/cwt) from " + BASIS_MEASUREMENT.n + " matched observations - not a producer-specific observed basis"); }

  // -- timeline ----------------------------------------------------------
  if (parts.eligible_count === 0) { q.push("insufficient"); reasons.push("no endorsement end date matches the modeled production timeline"); }

  return { quality: worstQuality(q), reasons, rules_version: DQ_RULES.version };
}

// ==========================================================================
// 7. ENTRY QUALITY - is TODAY'S net offer good, separately from the economics
// ==========================================================================
//
// Two independent reads, kept apart on purpose. "A profitable modeled
// opportunity exists" and "today is a particularly strong day to act" are
// different questions and v1 asked only the first while labelling the answer
// with the urgency of the second.
//
// (!) HORIZONS ARE NOT POOLED. A 13-week offer ranked against a 52-week offer
// is not a comparison - the premium and the forward shape are both different.
// Buckets below are compared like-for-like, and a bucket without enough history
// reports "not established" rather than a percentile drawn from nothing.

const ENTRY_RULES = {
  version: "entry-quality/2026-08-28",
  min_observations: 30, // below this a percentile ranks nothing
  strong_pctile: 80,
  weak_pctile: 20,
  deterioration_pctile: 70, // "was strong and has begun rolling over"
  horizon_buckets: [
    { key: "near", label: "13-21 wk", min_wk: 13, max_wk: 21 },
    { key: "mid", label: "22-34 wk", min_wk: 22, max_wk: 34 },
    { key: "far", label: "35-52 wk", min_wk: 35, max_wk: 52 },
  ],
};

function horizonBucket(weeks) {
  const w = Number(weeks);
  if (!Number.isFinite(w)) return null;
  return ENTRY_RULES.horizon_buckets.find((b) => w >= b.min_wk && w <= b.max_wk) || null;
}

// Percentile of `value` within `history`, floored on sample size. Returns null
// (never 50) when the sample is too thin - the same rule pctileOf follows in
// the MI briefs, and for the same reason: a percentile with no stated sample is
// not checkable.
function pctileOf(value, history, minN) {
  const h = (history || []).filter((v) => Number.isFinite(Number(v))).map(Number);
  const n = h.length;
  const floor = minN == null ? ENTRY_RULES.min_observations : minN;
  if (n < floor) return { pctile: null, n, floor, reason: "only " + n + " observations, floor is " + floor };
  const below = h.filter((v) => v < value).length;
  return { pctile: Math.round((below / n) * 100), n, floor, reason: null };
}

// history: [{date, weeks, net_index_floor}] for the SAME commodity/type.
function assessEntryQuality(todayNetFloor, weeks, history, opts) {
  const o = Object.assign({}, ENTRY_RULES, opts || {});
  const bucket = horizonBucket(weeks);
  if (!bucket) return { state: "not_established", reason: "endorsement length " + weeks + " wk falls in no horizon bucket", bucket: null };
  const inBucket = (history || []).filter((h) => {
    const b = horizonBucket(h.weeks);
    return b && b.key === bucket.key && Number.isFinite(Number(h.net_index_floor));
  });
  const byDate = new Map();
  for (const h of inBucket) {
    // one observation per day per bucket - the best net floor offered that day
    const prev = byDate.get(h.date);
    if (prev == null || Number(h.net_index_floor) > prev) byDate.set(h.date, Number(h.net_index_floor));
  }
  const dates = [...byDate.keys()].sort();
  const series = dates.map((d) => byDate.get(d));
  const p = pctileOf(todayNetFloor, series, o.min_observations);
  if (p.pctile == null) {
    return { state: "not_established", reason: p.reason, bucket: bucket.key, bucket_label: bucket.label, n: p.n };
  }
  const last = (k) => {
    if (dates.length <= k) return null;
    return byDate.get(dates[dates.length - 1 - k]);
  };
  const d5 = last(5) == null ? null : todayNetFloor - last(5);
  const d20 = last(20) == null ? null : todayNetFloor - last(20);
  const best = Math.max.apply(null, series);
  const fromBest = todayNetFloor - best;

  let state = "ordinary";
  if (p.pctile >= o.strong_pctile) state = "strong";
  else if (p.pctile <= o.weak_pctile) state = "weak";
  // Deterioration: still historically high but rolling over. This is the ONLY
  // timing condition that can create urgency, and it deliberately requires a
  // multi-day move rather than one volatile session.
  const deteriorating = p.pctile >= o.deterioration_pctile && d5 != null && d5 < 0 && d20 != null && d20 < 0;
  if (deteriorating) state = "deteriorating_from_strong";

  return {
    state, bucket: bucket.key, bucket_label: bucket.label,
    pctile: p.pctile, n: p.n, window_days: dates.length,
    change_5: d5, change_20: d20, best_net_floor: best, from_best: fromBest,
    rules_version: o.version, reason: null,
  };
}

// ==========================================================================
// 8. THE DECISION - five states, and the burden of proof rises with urgency
// ==========================================================================

const DECISION_THRESHOLDS = {
  version: MODEL_VERSION.decision_model,
  // Recommended after the sweep in tools/check-lrp-push.js --sweep; the sweep
  // itself is reported so the choice is inspectable rather than asserted.
  material_margin_hd: 50,
  material_return_pct: 0.02, // 2% of total invested cost, whichever is greater
  watch_band_hd: 0, // conservative >= this but < material -> WATCH
};

const DECISIONS = {
  NO_READ: { key: "NO_READ", label: "NO READ", tone: "neutral" },
  NO_PUSH: { key: "NO_PUSH", label: "NO PUSH", tone: "neutral" },
  WATCH: { key: "WATCH", label: "WATCH", tone: "amber" },
  CONTACT: { key: "CONTACT", label: "CONTACT", tone: "green" },
  PUSH_NOW: { key: "PUSH_NOW", label: "PUSH NOW", tone: "green" },
};

// `best` is the best-net-protected-margin candidate across eligible end dates
// and coverage levels, evaluated on the CONSERVATIVE scenario.
function decide(input) {
  const th = Object.assign({}, DECISION_THRESHOLDS, input.thresholds || {});
  const dq = input.data_quality;
  const why = [];

  // -- NO READ ---------------------------------------------------------
  if (!dq || dq.quality === "insufficient" || dq.quality === "stale") {
    return { decision: DECISIONS.NO_READ.key, why: (dq && dq.reasons) || ["no data-quality assessment"], thresholds: th, threshold_version: th.version };
  }
  if (!input.best) {
    return { decision: DECISIONS.NO_READ.key, why: ["no eligible endorsement to evaluate"], thresholds: th, threshold_version: th.version };
  }
  const b = input.best;
  if (b.conservative_margin_hd == null || b.standard_margin_hd == null) {
    return { decision: DECISIONS.NO_READ.key, why: ["margin could not be computed (premium, basis or cost missing)"], thresholds: th, threshold_version: th.version };
  }

  const material = Math.max(th.material_margin_hd, th.material_return_pct * (b.conservative_total_cost_hd || 0));
  const cons = b.conservative_margin_hd, std = b.standard_margin_hd;

  // -- NO PUSH ---------------------------------------------------------
  if (cons < th.watch_band_hd) {
    why.push("conservative protected margin is " + fmtHd(cons) + "/head, below $" + th.watch_band_hd + "/head");
    if (std > 0) why.push("the standard scenario is positive at " + fmtHd(std) + "/head, but the signal is judged on the conservative case");
    return { decision: DECISIONS.NO_PUSH.key, why, thresholds: th, material_threshold_hd: material, threshold_version: th.version };
  }

  // -- WATCH -----------------------------------------------------------
  if (cons < material) {
    why.push("conservative protected margin is " + fmtHd(cons) + "/head - positive but under the $" + Math.round(material) + "/head bar");
    if (input.entry && input.entry.state === "strong") why.push("today's net LRP offer is historically strong (" + input.entry.pctile + "th pctile, " + input.entry.bucket_label + ")");
    return { decision: DECISIONS.WATCH.key, why, thresholds: th, material_threshold_hd: material, threshold_version: th.version };
  }

  // -- CONTACT / PUSH NOW ----------------------------------------------
  why.push("conservative protected margin is " + fmtHd(cons) + "/head, clearing the $" + Math.round(material) + "/head bar");
  why.push("standard scenario " + fmtHd(std) + "/head");

  // (!) A FALLBACK INPUT CANNOT REACH CONTACT. The EEV backstop is a national
  // forward, not a local purchase cost - if that is what the purchase price came
  // from, the economics are not standing on a local observation and the honest
  // answer is WATCH, not a call list.
  if (dq.quality === "fallback") {
    why.unshift("input-animal price came from the EEV fallback, not NY cash - held at WATCH");
    return { decision: DECISIONS.WATCH.key, why, thresholds: th, material_threshold_hd: material, threshold_version: th.version };
  }

  const e = input.entry;
  const urgent = e && (e.state === "strong" || e.state === "deteriorating_from_strong");
  if (!urgent) {
    why.push(e && e.pctile != null
      ? "today's net LRP offer is ordinary for a " + e.bucket_label + " endorsement (" + e.pctile + "th pctile of " + e.n + " days) - no timing urgency"
      : "entry quality is not established (" + ((e && e.reason) || "no history") + ") - no timing urgency claimed");
    return { decision: DECISIONS.CONTACT.key, why, thresholds: th, material_threshold_hd: material, threshold_version: th.version };
  }
  why.push(e.state === "deteriorating_from_strong"
    ? "the net offer reached the " + e.pctile + "th pctile and has fallen " + fmtCwt(e.change_5) + "/cwt over 5 days and " + fmtCwt(e.change_20) + "/cwt over 20 - waiting has begun to cost"
    : "today's net LRP offer is at the " + e.pctile + "th pctile of " + e.n + " comparable " + e.bucket_label + " days");
  return { decision: DECISIONS.PUSH_NOW.key, why, thresholds: th, material_threshold_hd: material, threshold_version: th.version };
}

function fmtHd(v) { return (v >= 0 ? "+$" : "-$") + Math.abs(v).toFixed(0); }
function fmtCwt(v) { return v == null ? "n/a" : (v >= 0 ? "+$" : "-$") + Math.abs(v).toFixed(2); }

// ==========================================================================
// 9. THE ORCHESTRATOR - one call, every caller
// ==========================================================================
//
// Pure. No I/O, no clock, no network. Everything it needs arrives in `input`,
// which is what lets the API writer, the UI, the checker and the backtest all
// produce byte-identical results from the same inputs.

function buildPushSignal(input) {
  const {
    as_of, rate_date, segment, rates, feed, cash_rows, eev_cwt, history, thresholds,
    basis_override, scenario_overrides,
  } = input;

  const prog = PROGRAMS[segment];
  if (!prog) return { error: "unknown segment " + segment };

  // -- inputs ------------------------------------------------------------
  let cash = resolveInputAnimal(cash_rows, prog.in_wt_lb, { asOf: as_of });
  if (cash.price_cwt == null) {
    const fb = eevFallback(eev_cwt);
    fb.rejected_weeks = cash.rejected_weeks;
    fb.caveats = (cash.caveats || []).concat(fb.caveats || []);
    cash = fb;
  }

  const typeRates = (rates || []).filter((r) => r.type_label === prog.lrp_type_label && r.coverage_price != null);
  // A ragged end date is a partial ingestion. Detect it here so data quality can
  // fail closed rather than quietly computing on 1 coverage level of 12.
  const byEnd = new Map();
  for (const r of typeRates) {
    if (!byEnd.has(r.end_date)) byEnd.set(r.end_date, []);
    byEnd.get(r.end_date).push(r);
  }
  const ragged = [...byEnd.entries()]
    .filter(([, rows]) => rows.length < DQ_RULES.min_coverage_levels)
    .map(([end_date, rows]) => ({ end_date, n: rows.length }));
  // A whole end date that never arrived. Only judged when the type has a grid at
  // all — an empty grid is reported elsewhere, not as ten missing lengths.
  const presentWeeks = new Set(typeRates.map((r) => Number(r.endorsement_length_weeks)).filter(Number.isFinite));
  const missingLengths = typeRates.length ? STANDARD_ENDORSEMENT_WEEKS.filter((w) => !presentWeeks.has(w)) : [];

  // -- per scenario ------------------------------------------------------
  const scenarios = {};
  for (const key of SCENARIO_ORDER) {
    const sc = Object.assign({}, SCENARIOS[key], (scenario_overrides || {})[key] || {});
    const basisKey = basis_override || sc.basis_scenario;
    const basis = resolveBasis(basisKey, segment);
    const stack = buildCostStack(segment, key, { in_price_cwt: cash.price_cwt, feed, sale_price_cwt: null });
    scenarios[key] = { scenario: key, label: sc.label, blurb: sc.blurb, basis, cost: stack, adg: sc.adg[segment] };
  }
  const consBasis = scenarios.conservative.basis;

  // -- candidate endorsements -------------------------------------------
  const candidates = [];

  for (const r of typeRates) {
    // Each scenario has its own ADG, so each implies its own market-ready date
    // and therefore its own eligible end dates. ELIGIBILITY IS DECIDED ON THE
    // CONSERVATIVE TIMELINE because that is the scenario the decision is judged
    // on - but every scenario's own fit is carried, so a date that only works
    // if the cattle gain fast is visible as exactly that rather than silently
    // borrowed.
    const timingByScenario = {};
    for (const key of SCENARIO_ORDER) {
      timingByScenario[key] = endorsementTimingFit(as_of, r.end_date, prog.gain_lb, scenarios[key].adg);
    }
    const fitTiming = timingByScenario.conservative;
    const fitWeight = weightClassFits(prog.out_wt_cwt, r.target_low_weight, r.target_high_weight);
    const share = lrpProducerShare(r.coverage_level, r.reinsurance_year);
    const premCwt = producerPremiumCwt(r.cost_per_cwt, r.coverage_level, r.reinsurance_year);
    const nif = premCwt == null ? null : netIndexFloor(r.coverage_price, premCwt);

    const perScenario = {};
    for (const key of SCENARIO_ORDER) {
      const s = scenarios[key];
      const pcf = (premCwt == null || !s.basis) ? null : protectedCashFloor(r.coverage_price, premCwt, s.basis.cwt);
      const marginCwt = (pcf == null || !s.cost) ? null : pcf - s.cost.breakeven_cwt;
      perScenario[key] = {
        basis_cwt: s.basis ? s.basis.cwt : null,
        basis_key: s.basis ? s.basis.key : null,
        basis_provenance: s.basis ? s.basis.provenance : null,
        protected_cash_floor_cwt: pcf,
        breakeven_cwt: s.cost ? s.cost.breakeven_cwt : null,
        total_cost_hd: s.cost ? s.cost.total_cost_hd : null,
        margin_cwt: marginCwt,
        margin_hd: marginCwt == null ? null : marginCwt * prog.out_wt_cwt,
        return_pct: (marginCwt == null || !s.cost) ? null : (marginCwt * prog.out_wt_cwt) / s.cost.total_cost_hd,
      };
    }

    candidates.push({
      end_date: r.end_date,
      endorsement_weeks: r.endorsement_length_weeks,
      coverage_level: r.coverage_level,
      coverage_price_cwt: r.coverage_price,
      gross_premium_cwt: r.cost_per_cwt,
      producer_premium_cwt: premCwt,
      subsidy_share: share ? share.share : null,
      subsidy_pct: share ? share.subsidy : null,
      subsidy_verified: share ? share.verified : null,
      reinsurance_year: share ? share.reinsurance_year : null,
      subsidy_assumed_ry: share ? share.assumed_ry : null,
      net_index_floor_cwt: nif,
      timing: fitTiming,
      timing_by_scenario: timingByScenario,
      weight_fit: fitWeight,
      eligible: fitTiming.eligible && fitWeight.fits && premCwt != null,
      scenarios: perScenario,
    });
  }

  const eligible = candidates.filter((c) => c.eligible);
  const eligibleEndDates = [...new Set(eligible.map((c) => c.end_date))].sort();

  // (!) COVERAGE LEVELS ARE COMPARED ON NET PROTECTED ECONOMICS, NEVER ON THE
  // HIGHEST COVERAGE PRICE. v1 picked the best floor per length, which is always
  // 100% because breakeven was constant - so the table only ever showed the most
  // expensive endorsement and called it the best one.
  let best = null;
  for (const c of eligible) {
    const m = c.scenarios.conservative.margin_hd;
    if (m == null) continue;
    if (!best || m > best.conservative_margin_hd) {
      best = {
        end_date: c.end_date, endorsement_weeks: c.endorsement_weeks, coverage_level: c.coverage_level,
        coverage_price_cwt: c.coverage_price_cwt, producer_premium_cwt: c.producer_premium_cwt,
        net_index_floor_cwt: c.net_index_floor_cwt,
        conservative_margin_hd: m,
        conservative_margin_cwt: c.scenarios.conservative.margin_cwt,
        conservative_total_cost_hd: c.scenarios.conservative.total_cost_hd,
        standard_margin_hd: c.scenarios.standard.margin_hd,
        efficient_margin_hd: c.scenarios.efficient.margin_hd,
        basis_cwt: c.scenarios.conservative.basis_cwt,
        subsidy_verified: c.subsidy_verified,
        reinsurance_year: c.reinsurance_year,
        subsidy_assumed_ry: c.subsidy_assumed_ry,
        subsidy_share: c.subsidy_share,
      };
    }
  }
  // The subsidy caveat must describe the tier the DECISION rests on, not any
  // tier present in the grid. Every rate file carries 12 coverage levels
  // including ones nobody in this book has ever bought, so flagging on the scan
  // would put an "unverified subsidy" warning on every read forever - and a
  // warning that is always on is a warning nobody reads.
  const premiumUnverifiedTier = !!(best && best.subsidy_verified === false);
  // (!) THIS WAS HARDCODED false, SO THE RY CHECK NEVER RAN (fixed 2026-10-15).
  // The comment said lrp_daily_rates carries no reinsurance year; it has since
  // the 2026-08-28 rate file (RY2027 on every push-type row, measured) and the
  // writer already selected it. Same rule as the tier caveat: it describes the
  // endorsement the DECISION rests on. A missing year, or one past
  // verified_through_ry, reads Limited.
  const premiumAssumedRy = !!(best && best.subsidy_assumed_ry === true);

  // -- entry quality ------------------------------------------------------
  const entry = best
    ? assessEntryQuality(best.net_index_floor_cwt, best.endorsement_weeks, history || [])
    : { state: "not_established", reason: "no eligible endorsement", bucket: null };

  // -- data quality + decision -------------------------------------------
  const dq = assessDataQuality({
    as_of, rate_date, cash, feed_date: feed && feed.as_of_date,
    ragged_end_dates: ragged,
    missing_lengths: missingLengths,
    premium_unverified_tier: premiumUnverifiedTier,
    premium_assumed_ry: premiumAssumedRy,
    basis: consBasis,
    eligible_count: eligible.length,
  });

  const decision = decide({ best, entry, data_quality: dq, thresholds });

  // -- honest counts, replacing "91 / 120 endorsements in profit" ---------
  const counts = {
    rate_rows_for_type: typeRates.length,
    end_dates_offered: byEnd.size,
    eligible_end_dates: eligibleEndDates.length,
    eligible_end_date_list: eligibleEndDates,
    profitable_standard: eligibleEndDates.filter((d) => eligible.some((c) => c.end_date === d && c.scenarios.standard.margin_hd > 0)).length,
    profitable_conservative: eligibleEndDates.filter((d) => eligible.some((c) => c.end_date === d && c.scenarios.conservative.margin_hd > 0)).length,
    excluded_timeline: candidates.filter((c) => !c.timing.eligible).length,
    excluded_weight_class: candidates.filter((c) => c.timing.eligible && !c.weight_fit.fits).length,
  };

  return {
    as_of, rate_date, segment, program: prog,
    cash, feed, basis: consBasis, scenarios, candidates, eligible_end_dates: eligibleEndDates,
    best, entry, data_quality: dq, decision: decision.decision, decision_detail: decision,
    counts, model_version: MODEL_VERSION,
  };
}

// ==========================================================================
// 10. ALERTS - state TRANSITIONS only, never a daily restatement
// ==========================================================================
//
// A signal that mails the same decision every morning is filtered inside a week,
// and its silence then means nothing. So an alert fires on a CHANGE of state, on
// a decision going dark because the data went stale, or on a material
// deterioration in the conservative margin while the state itself holds.
//
// (!) DETERIORATION NEEDS ITS OWN BAR OR IT IS JUST NOISE. A conservative margin
// that drifts $3/head is not an alert; the default is $50/head or 25% of the
// prior margin, whichever is larger, so a big margin is not alerted on a
// rounding move and a small one is not alerted on a rounding move either.

const ALERT_RULES = {
  version: "alerts/2026-08-28",
  deterioration_hd: 50,
  deterioration_pct: 0.25,
};

function decisionTransition(prev, next, opts) {
  const o = Object.assign({}, ALERT_RULES, opts || {});
  const pd = prev && prev.decision, nd = next && next.decision;
  if (!nd) return { alert: false, reason: "no current decision" };
  // First observation of a segment is not a transition. Alerting on it would
  // mail the whole book the day this ships.
  if (!pd) return { alert: false, reason: "no prior state to transition from" };
  if (pd !== nd) {
    // Going dark is worth saying: a decision that WAS actionable and is now
    // unreadable is different from one that was always unreadable.
    if (nd === "NO_READ") return { alert: true, kind: "went_dark", from: pd, to: nd, reason: "inputs went stale or incomplete - the signal can no longer be read" };
    return { alert: true, kind: "state_change", from: pd, to: nd, reason: pd + " -> " + nd };
  }
  const a = Number(prev.conservative_margin_hd), b = Number(next.conservative_margin_hd);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    const drop = a - b;
    const bar = Math.max(o.deterioration_hd, Math.abs(a) * o.deterioration_pct);
    if (drop >= bar) {
      return { alert: true, kind: "deterioration", from: pd, to: nd, drop_hd: drop, bar_hd: bar, reason: "conservative protected margin fell " + drop.toFixed(0) + "/head (bar " + bar.toFixed(0) + ")" };
    }
  }
  return { alert: false, reason: "state unchanged (" + nd + ") and no material deterioration" };
}

// A threshold sweep, so the material bar is chosen on evidence rather than
// asserted. Returns what each candidate threshold would have decided.
function thresholdSweep(signal, bars) {
  const list = bars || [0, 25, 50, 100];
  return list.map((hd) => {
    const d = decide({ best: signal.best, entry: signal.entry, data_quality: signal.data_quality, thresholds: { material_margin_hd: hd, material_return_pct: 0 } });
    return { material_margin_hd: hd, decision: d.decision };
  });
}

module.exports = {
  MODEL_VERSION, LRP_SUBSIDY_SCHEDULE, lrpProducerShare, producerPremiumCwt,
  netIndexFloor, protectedCashFloor,
  BASIS_SCENARIOS, BASIS_MEASUREMENT, resolveBasis,
  BAND_MID_LB, CASH_RULES, interpolateSlide, resolveInputAnimal, eevFallback,
  MARKETING_WINDOW_DAYS, TIMELINE_RULES, addDays, daysBetween, STANDARD_ENDORSEMENT_WEEKS,
  requiredDaysOnFeed, impliedAdg, endorsementTimingFit, weightClassFits,
  SCENARIOS, SCENARIO_ORDER, PROGRAMS,
  fedCogCommercial, feederCogCommercial, cogPerLb, ownershipCostsBeyondFeed, buildCostStack,
  DQ_ORDER, DQ_RANK, DQ_RULES, worstQuality, assessDataQuality,
  ENTRY_RULES, horizonBucket, pctileOf, assessEntryQuality,
  DECISION_THRESHOLDS, DECISIONS, decide, buildPushSignal, thresholdSweep,
  ALERT_RULES, decisionTransition,
};

